import { useEffect, useState } from 'react'

import { Image } from 'antd'

import { ImageDisplayBlock as ImageDisplayBlockType } from '../../types/blueprint'
import { DisplayAttachments } from '../DisplayAttachments'

import { Text } from '@/components/shared-components/BUI/Text'
import DocumentViewer from '@/components/shared-components/DocumentViewer'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { isFileImage } from '@/utils/fetchFileType'

enum Mode {
  Pdf = 'pdf',
  Image = 'image',
  Loading = 'loading',
}

const IMAGE_SIZE = 150

type Props = {
  block: ImageDisplayBlockType
}

export const ImageDisplayBlock: React.FC<Props> = ({ block }) => {
  const [mode, setMode] = useState(Mode.Loading)
  const { renderOptions } = useTemplateRenderer()

  const { url, altText, caption, thumbnailUrl } = block

  useEffect(() => {
    // if rendering in PDF, let DisplayAttachments do its thing
    if (url && !renderOptions.renderingInPdf) {
      isFileImage(url).then((isImg) => {
        setMode(isImg ? Mode.Image : Mode.Pdf)
      })
    }
  }, [url, renderOptions.renderingInPdf])

  const renderFile = () => {
    if (!url) return null

    if (renderOptions.renderingInPdf) {
      return (
        <DisplayAttachments
          title=""
          renderingInPdf
          attachments={[
            {
              id: block.id,
              url: block.url,
              thumbnailUrl: block.thumbnailUrl,
              name: block.altText,
            },
          ]}
        />
      )
    }

    if (mode === Mode.Pdf) {
      return <DocumentViewer url={url} name={altText} contentType={'pdf'} />
    }

    if (mode === Mode.Image) {
      return (
        <Image
          src={thumbnailUrl || url}
          alt={altText}
          preview={{ src: url, mask: <p /> }}
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          onError={() => setMode(Mode.Pdf)}
          style={{ objectFit: 'cover' }}
          wrapperStyle={{ borderRadius: '0.25rem', overflow: 'hidden' }}
        />
      )
    }

    return null
  }

  return (
    <>
      {renderFile()}

      {caption && (
        <Text variant="caption01" secondary="_500" block>
          {caption}
        </Text>
      )}
    </>
  )
}
